import { useState } from 'react';
import { Box, Container, Paper, Typography, Alert, Button, Stack } from '@mui/material';
import { Bolt, CheckCircle } from '@mui/icons-material';
import { dealerApplicationsApi } from '../../../api/dealerApplications.api';
import { extractApiError } from '../../../api/error';
import { DealerRegistrationForm } from './DealerRegistrationForm';

/**
 * PUBLIC dealer/partner self-registration — no login required. Submits a dealer
 * application that the PDI team verifies → approves (which provisions the dealer
 * + login). Standalone page (outside the authenticated app shell).
 */
export function PublicDealerRegistrationPage() {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ applicationCode: string } | null>(null);

  const submit = async (payload: Record<string, unknown>) => {
    setSubmitting(true); setError(null);
    try {
      const res = await dealerApplicationsApi.publicSubmit(payload);
      setDone({ applicationCode: res.applicationCode });
    } catch (e) {
      setError(extractApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="md">
        <Stack direction="row" alignItems="center" gap={1.25} mb={2}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Bolt /></Box>
          <Box><Typography fontWeight={800} fontSize={18}>PDI Partner / Dealer Registration</Typography><Typography variant="caption" color="text.secondary">Protection Device Installation — Channel Partner Onboarding</Typography></Box>
        </Stack>

        {done ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircle color="success" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h6" gutterBottom>Application Submitted</Typography>
            <Typography color="text.secondary">Your reference: <strong>{done.applicationCode}</strong></Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>Our team will review your application and get in touch shortly.</Typography>
            <Button sx={{ mt: 3 }} onClick={() => setDone(null)}>Submit another</Button>
          </Paper>
        ) : (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>Fill in your details below. Fields marked required must be completed. After submission our team will verify &amp; approve your partnership.</Alert>
            <DealerRegistrationForm showLogin submitting={submitting} error={error} submitLabel="Submit Registration" onSubmit={submit} />
          </Paper>
        )}
      </Container>
    </Box>
  );
}
